/**
 * Frontend tax calculation for rental transactions.
 * Mirrors backend tax-rates.ts logic for display purposes.
 * Backend is the source of truth — this is for preview only.
 *
 * Optional duration rules:
 *   max_days — tax only applies if rental days <= this value
 *   min_days — tax only applies if rental days >= this value
 */

// Map store_id → zip code
const STORE_ZIP_MAP = {
  1: '46240', // Indianapolis
  2: '46240', // Indianapolis
  3: '15213', // Pittsburgh
  4: '63143', // St. Louis
  5: '47901', // Lafayette
  6: '75075', // Dallas / Plano
};

const TAX_RATES = {
  // Indianapolis (stores 1 & 2) — 7% state + 2% car sharing; car sharing waived for 30+ day rentals
  '46240': [
    { name: 'IN State Sales Tax', type: 'percentage', rate: 0.07 },
    { name: 'Car Sharing Tax', type: 'percentage', rate: 0.02, max_days: 29 },
  ],
  // Lafayette (store 5) — same as Indianapolis
  '47901': [
    { name: 'IN State Sales Tax', type: 'percentage', rate: 0.07 },
    { name: 'Car Sharing Tax', type: 'percentage', rate: 0.02, max_days: 29 },
  ],
  // Pittsburgh (store 3)
  '15213': [
    { name: 'PA State Sales Tax', type: 'percentage', rate: 0.07 },
    { name: 'Vehicle Rental Fee', type: 'per_day', rate: 2.00 },
  ],
  // St. Louis (store 4)
  '63143': [
    { name: 'Combined Sales Tax', type: 'percentage', rate: 0.0968 },
  ],
  // Dallas / Plano (store 6) — Texas does not apply sales tax to motor vehicle
  // rentals. Ch. 152 gross rental receipts tax applies instead: 10% for 1-30
  // days, 6.25% for 31-180 days. Past 180 days the contract is a lease, not a
  // rental, and the payments are untaxed (the lessor already paid motor vehicle
  // sales tax at titling) — hence the 180-day cap, which lets longer contracts
  // fall through to $0. Collin County and Plano levy no local venue rental tax.
  '75075': [
    { name: 'TX Motor Vehicle Rental Tax', type: 'percentage', rate: 0.10, max_days: 30 },
    { name: 'TX Motor Vehicle Rental Tax', type: 'percentage', rate: 0.0625, min_days: 31, max_days: 180 },
  ],
};

/**
 * Calculate tax for frontend display.
 * @param {number} storeId - Store ID
 * @param {number} amount - Rental amount (before tax)
 * @param {number} days - Number of rental days
 * @returns {{ totalTax: number, items: Array<{ name: string, type: string, rate: number, amount: number }> }}
 */
export function calculateTaxFrontend(storeId, amount, days) {
  const zipCode = STORE_ZIP_MAP[storeId];
  if (!zipCode) return { totalTax: 0, items: [] };

  const rates = TAX_RATES[zipCode];
  if (!rates || rates.length === 0) return { totalTax: 0, items: [] };

  const items = [];

  for (const entry of rates) {
    // Check duration rules
    if (entry.max_days !== undefined && days > entry.max_days) continue;
    if (entry.min_days !== undefined && days < entry.min_days) continue;

    let taxAmount;
    if (entry.type === 'percentage') {
      taxAmount = Math.round(amount * entry.rate * 100) / 100;
    } else {
      // per_day
      taxAmount = Math.round(entry.rate * days * 100) / 100;
    }
    items.push({
      name: entry.name,
      type: entry.type,
      rate: entry.rate,
      amount: taxAmount,
    });
  }

  const totalTax = Math.round(items.reduce((sum, item) => sum + item.amount, 0) * 100) / 100;

  return { totalTax, items };
}
